import { nextStart, pcm16ToFloat, TARGET_RATE } from './pcm'

export interface Playback {
  /** Schedule one inbound chunk. The agent sends fixed 1280-byte (40 ms) ones. */
  push: (chunk: ArrayBuffer) => void
  /** Cut everything queued, immediately, and re-open the mic gate. */
  stopAll: () => void
  /** True while scheduled speech is still playing, plus the tail. */
  isBotSpeaking: () => boolean
  /** Output spectrum, for the orb. */
  analyser: AnalyserNode
  dispose: () => void
}

interface PlaybackOptions {
  /** Jitter budget. Scheduling at currentTime exactly gets the chunk truncated. */
  playbackLeadMs: number
  /** Guard band after the last sample before the mic re-opens. */
  ttsTailMs: number
}

export function createPlayback(
  ctx: AudioContext,
  { playbackLeadMs, ttsTailMs }: PlaybackOptions,
): Playback {
  // Everything routes through the analyser so the orb sees the agent's voice;
  // it is also the only node connected to the speakers.
  const analyser = ctx.createAnalyser()
  analyser.fftSize = 512
  analyser.connect(ctx.destination)

  // Where the scheduled audio currently runs out, in ctx.currentTime seconds.
  let playHead = 0
  let queued: AudioBufferSourceNode[] = []

  return {
    analyser,

    push(chunk) {
      const samples = pcm16ToFloat(new Int16Array(chunk))
      const buffer = ctx.createBuffer(1, samples.length, TARGET_RATE)
      buffer.getChannelData(0).set(samples)

      const source = ctx.createBufferSource()
      source.buffer = buffer
      source.connect(analyser)

      const at = nextStart(ctx.currentTime, playHead, playbackLeadMs / 1000)
      source.start(at)
      playHead = at + buffer.duration

      queued.push(source)
      source.onended = () => {
        queued = queued.filter((s) => s !== source)
      }
    },

    stopAll() {
      for (const source of queued) {
        try {
          source.stop()
        } catch {
          // A source that already ended throws; harmless.
        }
      }
      queued = []
      // Zero, not currentTime. This is what re-opens the mic gate immediately —
      // isBotSpeaking() compares against currentTime, so any past value works,
      // and it also makes the next chunk re-pay the jitter budget.
      playHead = 0
    },

    // Half duplex on purpose. There is no echo canceller in this loop, so an
    // open mic means the agent hears itself through the phone's loudspeaker,
    // interrupts itself, and transcribes its own voice into the order.
    //
    // The agent is specified to send bot_started/bot_stopped frames, which would
    // replace this — but they do not exist in the shipped agent, so inference
    // from the play head is the mechanism, not a stopgap. Swapping it later
    // means changing this one function.
    isBotSpeaking() {
      return playHead + ttsTailMs / 1000 > ctx.currentTime
    },

    dispose() {
      this.stopAll()
      try {
        analyser.disconnect()
      } catch {
        // Already torn down.
      }
    },
  }
}
